import dynamic from 'next/dynamic';
import Head from 'next/head';
import styled from 'styled-components';
import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { FILTER_ENUMS } from 'search/enums/constants';
import { formatDate } from 'shared/utils/formatDate';
import {
  GRIDS_WIDTH,
  SUPER_COMPRESSED_DESKTOP_HEADER_HEIGHT_NUMBER,
} from 'search/styles/SearchStyles';
import { getPageNumber } from 'search/utils/getPageNumber';
import { Grid } from 'shared/components/Grid';
import { GridHeader } from 'shared/components/Grid/GridHeader';
import { hideScrollbars } from 'styles/utils/hideScrollbars';
import { IBreadcrumb } from 'shared/types/Breadcrumbs';
import { IConstructorBrowseResponse } from 'shared/types/ConstructorBrowseResponse';
import { IConstructorSearchResponse } from 'shared/types/ConstructorSearchResponse';
import { ISubCollectionList } from 'shared/types/ConsumerSearch';
import { MOBILE_BANNER_HEIGHT } from 'banner/enums/constants';
import { MOBILE_HEADER_HEIGHT, NEW_NAV_MOBILE_HEADER_HEIGHT } from 'layout/header/enums/constants';
import { nonRefElementInViewport } from 'search/utils/nonRefElementInViewport';
import { SearchNoResults } from 'search/components/searchGrid/SearchNoResults';
import SearchSortAndFiltersDesktop from 'search/components/searchGrid/SearchSortAndFiltersDesktop';
import { SearchSortAndFiltersMobile } from 'search/components/searchGrid/SearchSortAndFiltersMobile';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { showOnDesktopOnly, showOnMobileAndTabletOnly } from 'styles/utils/responsiveness';
import { t } from 'localization/utils/t';
import { TIMELINE_PATHNAME } from 'timeline/enums/constants';
import { TimelineNavigation } from 'timeline/components/TimelineNavigation';
import { toggleVisible } from 'styles/theme/animation';
import { useEventContext } from 'tracking/hooks/useEventContext';
import { useGridScrollEvent } from 'search/hooks/useGridScrollEvent';
import { useInfiniteConstructorSearch } from 'search/hooks/useInfiniteConstructorSearch';
import { useIsBannerVisible } from 'shared/hooks/useIsBannerVisible';
import { useIsElementInViewport } from 'shared/hooks/useIsElementInViewport';
import { useIsMounted } from 'shared/hooks/useIsMounted';
import { usePersistGridScrollPosition } from 'search/hooks/usePersistGridScrollPosition';
import { usePutBackHeaderOnPageChange } from 'shared/hooks/usePutBackHeaderOnPageChange';
import { useShowScrollToTopArrow } from 'search/hooks/useShowScrollToTopArrow';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { useTimeline } from 'timeline/hooks/useTimeline';
import { useToggleHeaderOnScrollEvent } from 'shared/hooks/useToggleHeaderOnScrollEvent';
import { useWindowSize } from 'shared/hooks/useWindowSize';

const TP = 'search.components.searchGrid.SearchGrid';

const SearchGridBreadcrumbs = dynamic(() =>
  import('search/components/searchGrid/SearchGridBreadcrumbs').then(
    (mod) => mod.SearchGridBreadcrumbs,
  ),
);
const ScrollToTopArrow = dynamic(
  () => import('shared/components/ScrollToTopArrow').then((mod) => mod.ScrollToTopArrow),
  { ssr: false },
);
const Loader = dynamic(() => import('shared/components/Loader').then((mod) => mod.Loader));

interface ISearchGrid {
  brandStoryVisible?: boolean;
  breadcrumbs?: IBreadcrumb[];
  browseKey?: string;
  browseValue?: string;
  constructorResponse: IConstructorBrowseResponse | IConstructorSearchResponse;
  description?: string;
  globalPricingId: string;
  hasLink?: boolean;
  hideMetadata?: boolean;
  isCrawler: boolean;
  linkDestination?: string;
  linkTitle?: string;
  noIndex?: boolean;
  subCollections?: ISubCollectionList;
  timelineBreadcrumbs?: IBreadcrumb[];
  timelineYears?: Array<string>;
  title?: string;
}

export const SearchGrid = ({
  brandStoryVisible,
  breadcrumbs,
  browseKey,
  browseValue,
  constructorResponse,
  description,
  globalPricingId,
  hasLink,
  hideMetadata = false,
  linkDestination,
  linkTitle,
  noIndex = false,
  title,
  timelineBreadcrumbs,
  isCrawler,
  subCollections,
}: ISearchGrid) => {
  const observerRef = useRef();
  const shouldLoadMore = useIsElementInViewport(observerRef);
  const isMounted = useIsMounted();
  const { isMobile } = useWindowSize();
  const { isHeaderVisible } = useStoreSelector((state) => state.pageLayout);
  const isBannerVisible = useIsBannerVisible();
  const { asPath, pathname, query } = useRouter();
  const releaseDate = query?.release_date as string;
  const isUpcomingRelease =
    releaseDate && query?.sortBy === 'release_date' && query?.sortOrder === 'ascending';
  const isSearchPath = pathname === '/search';
  const isTimelinePath = pathname === TIMELINE_PATHNAME;
  const eventTrackingEventContext = useEventContext();
  const showNoindexTag = asPath?.split('?')?.length > 1 || noIndex;
  const [isFooterVisible, setIsFooterVisible] = useState(false);
  const [areBreadcrumbsVisible, setAreBreadcrumbsVisible] = useState(true);
  const [areFiltersVisible, setAreFiltersVisible] = useState(false);

  useToggleHeaderOnScrollEvent(false, hideMetadata && brandStoryVisible && isMobile);
  usePutBackHeaderOnPageChange();

  const { timelineId, timelineIdType, timelineTitle, timelineDescription, timelineYears } =
    useTimeline(timelineBreadcrumbs);

  const {
    facets,
    groups,
    resultIds,
    results,
    resultsCount,
    setSize,
    size,
    sortOptions,
    searchQuery,
  } = useInfiniteConstructorSearch({
    fallbackData: [constructorResponse],
    browseKey: isTimelinePath ? timelineIdType : browseKey,
    browseValue: isTimelinePath ? timelineId : browseValue,
    globalPricingId,
  });

  const priceFacet = facets?.find((facet) => facet.name === FILTER_ENUMS.PRICE);
  const currencyRates = priceFacet?.data?.currency_rates || null;

  const gridTitle = () => {
    if (isTimelinePath) {
      return timelineTitle;
    }

    // COMM-339: We show this text when navigating from PDP's Nutrition Facts Release Date
    // We do not want to show this when Upcoming Releases is selected from mobile Years filter
    // which would have the sort options applied
    if (releaseDate && !isUpcomingRelease) {
      return `Release Date ${formatDate.monthYear(/^.{1,8}/.exec(releaseDate)[0])}`;
    }

    return title || searchQuery || t(`${TP}.shopAll`, 'Shop All');
  };

  const { alwaysCompressHeader, superCompressHeader } = useGridScrollEvent({
    compare: [title, searchQuery],
    alwaysCompressHeader:
      isSearchPath || isTimelinePath || !description || description?.length <= 0 || hideMetadata,
  });

  // watch for click on filter, check height of desktop
  // if filters extended on desktop, hide  breadcrumbs
  const handleClickInsidePage = (e) => {
    const attrValue = e.target.getAttribute('data-qa');

    if (attrValue?.includes('filter') && document) {
      const desktopFilters = document.querySelector<HTMLElement>('#desktop-filters');
      const desktopFiltersHeight = desktopFilters?.offsetHeight;
      if (isMounted()) {
        setAreBreadcrumbsVisible(desktopFiltersHeight < 400);
      }
    }
  };

  usePersistGridScrollPosition({ setSize });
  const showScrollToTopArrow = useShowScrollToTopArrow([JSON.stringify(results)]);

  useEffect(() => {
    if (shouldLoadMore && resultsCount > results.length) {
      void setSize(Number(size) + 1);
    }
  }, [shouldLoadMore]);

  useEffect(() => {
    sendTrackingEvent('CONSTRUCTOR_SEE_MORE_SCROLL', {
      event_context: eventTrackingEventContext,
      page_number: getPageNumber(results.length),
    });
  }, [results.length]);

  useEffect(() => {
    const handleScrollEvent = () => {
      setTimeout(() => {
        if (isMounted()) {
          if (!isMobile) {
            const desktopFilters = document.querySelector<HTMLElement>('#desktop-filters');
            const desktopFiltersHeight = desktopFilters?.offsetHeight;
            setAreBreadcrumbsVisible(desktopFiltersHeight < 400);

            // get the position of the footer, and when it's in view
            // make sure the breadcrumbs are no longer fixed, but absolute
            const siteFooter = document.querySelector<HTMLElement>('#site-footer');
            const footerStatus = nonRefElementInViewport(siteFooter, 58);
            setIsFooterVisible(footerStatus);
          }

          const bodyDistanceFromTop = document.querySelector<HTMLElement>('#grid-body')?.offsetTop;
          const currentScroll = window.pageYOffset || document.documentElement.scrollTop;

          if (
            bodyDistanceFromTop - SUPER_COMPRESSED_DESKTOP_HEADER_HEIGHT_NUMBER <=
            currentScroll
          ) {
            setAreFiltersVisible(true);
          } else {
            setAreFiltersVisible(false);
          }
        }
      }, 300);
    };

    document.addEventListener('scroll', handleScrollEvent);

    return () => {
      document.removeEventListener('scroll', handleScrollEvent);
    };
  });

  return (
    <SearchGrid.Container
      data-qa="search_grid_container"
      $lockScroll={brandStoryVisible && !isMobile && hideMetadata}
      onClick={handleClickInsidePage}
    >
      <Head>{showNoindexTag && <meta name="robots" content="noindex" />}</Head>
      <SearchGrid.Header
        id="header"
        $isHeaderVisible={isHeaderVisible}
        $isStickyAndSuperCompressed={superCompressHeader || hideMetadata}
        $isTimelinePath={isTimelinePath}
      >
        {(!isMobile || (description && description?.length > 0)) && (
          <GridHeader
            count={resultsCount}
            description={timelineDescription || description}
            hasLink={hasLink}
            hideMetadata={hideMetadata}
            isHeaderVisible={isHeaderVisible}
            isSearchPath={isSearchPath}
            isStickyAndSuperCompressed={superCompressHeader || hideMetadata}
            isTimelinePath={isTimelinePath}
            linkDestination={linkDestination}
            linkTitle={linkTitle}
            resultIds={resultIds}
            title={gridTitle()}
          />
        )}
        {isMobile && (
          <SearchGrid.SearchSortFiltersAndHeaderMobileWrapper
            $alwaysCompressHeader={
              (isHeaderVisible && alwaysCompressHeader) || (isHeaderVisible && hideMetadata)
            }
            $showFilters={isHeaderVisible && areFiltersVisible}
            $shouldShowFiltersAtTop={!isHeaderVisible}
            $isBannerVisible={isBannerVisible}
          >
            <GridHeader
              count={resultsCount}
              description={timelineDescription || description}
              hasLink={hasLink}
              isSearchPath={isSearchPath}
              isStickyAndSuperCompressed
              isTimelinePath={isTimelinePath}
              linkDestination={linkDestination}
              linkTitle={linkTitle}
              resultIds={resultIds}
              title={gridTitle()}
            />
            <SearchGrid.SearchSortAndFiltersMobileWrapper data-qa="mobile_sort_filter_wrapper">
              <SearchSortAndFiltersMobile
                facets={facets}
                groups={groups}
                resultIds={resultIds}
                sortOptions={sortOptions}
                isCrawler={isCrawler}
                subCollections={subCollections}
              />
            </SearchGrid.SearchSortAndFiltersMobileWrapper>
          </SearchGrid.SearchSortFiltersAndHeaderMobileWrapper>
        )}
        {isTimelinePath && (
          <TimelineNavigation
            timelineYears={timelineYears}
            showTimelineNavigationOnMobile={isHeaderVisible}
          />
        )}
      </SearchGrid.Header>
      <SearchGrid.Body
        id="grid-body"
        $alwaysCompressHeader={alwaysCompressHeader}
        $showExpandedHeader={description && description?.length > 0 && !isTimelinePath}
        $isStickyAndSuperCompressed={superCompressHeader}
        $isTimelinePath={isTimelinePath}
        $isBrandStoryGrid={hideMetadata}
      >
        <SearchGrid.LeftAside $showFilters>
          {!isMobile && (
            <SearchSortAndFiltersDesktop
              facets={facets}
              groups={groups}
              hideMetadata={hideMetadata}
              sortOptions={sortOptions}
              isStickyAndSuperCompressed={superCompressHeader}
              isTimelinePath={isTimelinePath}
              resultIds={resultIds}
              isCrawler={isCrawler}
              subCollections={subCollections}
            />
          )}
          {breadcrumbs && !isMobile && (
            <SearchGrid.BreadcrumbContainer
              id="grid-breadcrumbs"
              $isOverlapping={isFooterVisible}
              $isVisible={areBreadcrumbsVisible && areFiltersVisible}
            >
              <SearchGridBreadcrumbs breadcrumbs={breadcrumbs} />
            </SearchGrid.BreadcrumbContainer>
          )}
        </SearchGrid.LeftAside>
        <SearchGrid.Wrapper>
          {results.length === 0 ? (
            <SearchNoResults />
          ) : (
            <Grid
              browseKey={browseKey}
              browseValue={browseValue}
              currencyRates={currencyRates}
              disableLazyLoad={hideMetadata && !isMobile}
              globalPricingId={globalPricingId}
              products={results}
              resultsCount={resultsCount}
            />
          )}
          <SearchGrid.Observer ref={observerRef} />
          {results?.length < resultsCount && (
            <Loader style={{ width: '40%', margin: '40px auto' }} />
          )}
          {breadcrumbs && isMobile && (
            <SearchGrid.MobileBreadcrumbContainer>
              <SearchGridBreadcrumbs breadcrumbs={breadcrumbs} />
            </SearchGrid.MobileBreadcrumbContainer>
          )}
        </SearchGrid.Wrapper>
        <SearchGrid.RightAside>
          <SearchGrid.ScrollToTop $isVisible={showScrollToTopArrow}>
            {!hideMetadata && <ScrollToTopArrow />}
          </SearchGrid.ScrollToTop>
        </SearchGrid.RightAside>
      </SearchGrid.Body>
      {hideMetadata && !isMobile && <SearchGrid.FillerEllement />}
    </SearchGrid.Container>
  );
};

SearchGrid.Container = styled.div<{ $lockScroll: boolean }>`
  position: ${({ $lockScroll }) => ($lockScroll ? `fixed` : `relative`)};
  width: 100%;
  background-color: ${({ theme }) => theme.navigation.backgroundColor};
`;

SearchGrid.Header = styled.div<{
  $isHeaderVisible?: boolean;
  $isStickyAndSuperCompressed?: boolean;
  $isTimelinePath?: boolean;
}>`
  background: ${({ theme }) => theme.navigation.backgroundColor};
  top: ${({ $isHeaderVisible }) => ($isHeaderVisible ? NEW_NAV_MOBILE_HEADER_HEIGHT : `0px`)};
  z-index: ${({ theme }) => theme.zIndex.searchGridHeader};

  ${({ $isStickyAndSuperCompressed, $isTimelinePath }) =>
    $isTimelinePath && $isStickyAndSuperCompressed && `position: relative;`};
  ${({ $isTimelinePath }) => $isTimelinePath && `border-bottom: none;`};

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    border-bottom: none;
    margin: auto;
    position: relative;
    top: 0;
    width: 100%;
  }

  ${GridHeader.Title} {
    @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
      margin-top: 0;
    }
  }
`;

SearchGrid.SearchSortFiltersAndHeaderMobileWrapper = styled.div<{
  $alwaysCompressHeader: boolean;
  $isBannerVisible?: boolean;
  $shouldShowFiltersAtTop?: boolean;
  $showFilters: boolean;
}>`
  ${showOnMobileAndTabletOnly`
    background: ${({ theme }) => theme.navigation.backgroundColor};
    margin-bottom: 18px;
    border-bottom: 1px solid ${({ theme }) => theme.navigation.borderColor};
    overflow: visible;
    min-height: 30px;
    position: fixed;
    top: ${({ $alwaysCompressHeader, $isBannerVisible, $showFilters, $shouldShowFiltersAtTop }) => {
      const height = `calc(${NEW_NAV_MOBILE_HEADER_HEIGHT}
        + ${$isBannerVisible ? MOBILE_BANNER_HEIGHT : '0px'})
        `;

      if ($shouldShowFiltersAtTop) {
        return 0;
      }

      return $showFilters || $alwaysCompressHeader ? height : `-130px`;
    }};
    padding: 16px 0px;
    width: 100%;

    z-index: ${({ theme }) => theme.zIndex.searchGridHeader};
    transition: all 0.3s ease-in;
    ${hideScrollbars};
  `};
`;

SearchGrid.SearchSortAndFiltersMobileWrapper = styled.div`
  width: 100vw;
  white-space: nowrap;
  overflow-x: scroll;
  ${hideScrollbars};
`;

SearchGrid.Body = styled.div<{
  $alwaysCompressHeader: boolean;
  $showExpandedHeader: boolean;
  $isStickyAndSuperCompressed: boolean;
  $isTimelinePath: boolean;
  $isBrandStoryGrid: boolean;
}>`
  border-top: 1px solid ${({ theme }) => theme.navigation.borderColor};
  position: relative;
  display: flex;
  margin-bottom: ${MOBILE_HEADER_HEIGHT};

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    ${({ $alwaysCompressHeader }) => $alwaysCompressHeader && `top: 94px;`};
    ${({ $isBrandStoryGrid }) => $isBrandStoryGrid && `top: 104px;`};
    ${({ $isTimelinePath }) => $isTimelinePath && `top: 111px; margin-bottom: 84px;`}
    ${({ $showExpandedHeader }) => !$showExpandedHeader && `top: 124px;`};
    ${({ $isTimelinePath, $showExpandedHeader }) =>
      $isTimelinePath && !$showExpandedHeader && `top: 144px;`};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    border-top: none;
  }

  ${({ $isStickyAndSuperCompressed }) => $isStickyAndSuperCompressed && `margin-top: 0px;`}
`;

SearchGrid.Wrapper = styled.div`
  width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    width: ${GRIDS_WIDTH};
  }
`;

SearchGrid.ScrollToTop = styled.div<{ $isVisible: boolean }>`
  ${showOnDesktopOnly`
    position: sticky;
    align-self: flex-end;
    bottom: 0;
    padding: 38px 14px;
    opacity: 0;
    visibility: hidden;
    ${({ $isVisible }) => $isVisible && toggleVisible};
  `}
`;

SearchGrid.Observer = styled.div`
  position: absolute;
  bottom: 0;
  height: 900px;
`;

SearchGrid.Aside = styled.aside<{ $showFilters?: boolean }>`
  ${showOnDesktopOnly`
    width: calc((100% - ${GRIDS_WIDTH})/2);
  `}
`;

SearchGrid.LeftAside = styled(SearchGrid.Aside)`
  ${showOnDesktopOnly`
    opacity: ${({ $showFilters }) => ($showFilters ? `1` : `0`)};
    transition: all 0.2s ease-in;
  `}
`;

SearchGrid.RightAside = styled(SearchGrid.Aside)`
  ${showOnDesktopOnly`
    display: flex;
    justify-content: flex-end;
  `}
`;

SearchGrid.BreadcrumbContainer = styled.div<{ $isOverlapping?: boolean; $isVisible: boolean }>`
  ${({ $isOverlapping }) => showOnDesktopOnly`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-end;
    padding-right: 16px;
    ${$isOverlapping ? `position: absolute;` : `position: fixed;`}
    text-align: right;
    width: calc((100% - ${GRIDS_WIDTH})/2);
  `};

  transition: all 0.3s;
  transition-timing-function: ease-in;

  ${({ $isVisible, $isOverlapping }) =>
    $isVisible
      ? `opacity: 1; ${
          $isOverlapping
            ? `
    bottom: 0px;`
            : `
    bottom: 36px;`
        } `
      : 'opacity: 0; bottom: -300px;'}
`;

SearchGrid.MobileBreadcrumbContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-end;
  margin-bottom: 72px;
  margin-top: 24px;
  padding-left: 16px;
`;

SearchGrid.FillerEllement = styled.div`
  height: 50px;
  width: 100vw;
`;
